/**
 * Demo asset fallback service for GenFlappy
 *
 * Provides fallback demo assets when AI-generated assets fail quality validation.
 * Uses pre-verified assets from robert-kratz/flappy-bird (Apache-2.0 license).
 *
 * @module services/demoAssets
 */

import type { AssetType } from '../types/validation';
import { logError } from './errorDetectionService';

/**
 * Map of asset types to their demo asset file paths
 *
 * Paths are absolute and served from public/demo-assets/
 */
const DEMO_ASSET_PATHS: Record<AssetType, string> = {
  character: '/demo-assets/bird.png',
  obstacle: '/demo-assets/pipe-north.png',
  background: '/demo-assets/background-day.png',
} as const;

/**
 * Get the file path for a demo asset
 *
 * Returns absolute path to demo asset in public/demo-assets/ directory.
 * These assets are pre-verified and always pass quality validation.
 *
 * @param {AssetType} assetType - Type of asset (character, obstacle, background)
 * @returns {string} Absolute path to demo asset file
 *
 * @example
 * ```typescript
 * const path = getDemoAssetPath('character');
 * // Returns: '/demo-assets/bird.png'
 * ```
 */
export function getDemoAssetPath(assetType: AssetType): string {
  return DEMO_ASSET_PATHS[assetType];
}

/**
 * Load a demo asset and convert it to a base64 data URL
 *
 * Loads the demo asset from public/demo-assets/ and converts it to a
 * base64-encoded data URL for use in the game. Handles loading errors
 * gracefully by returning a transparent 1x1 PNG as fallback.
 *
 * @param {AssetType} assetType - Type of asset to load
 * @returns {Promise<string>} Base64 data URL (e.g., "data:image/png;base64,...")
 *
 * @throws {Error} If image fails to load (after returning fallback)
 *
 * @example
 * ```typescript
 * const dataUrl = await loadDemoAsset('character');
 * // Returns: "data:image/png;base64,iVBORw0KG..."
 * ```
 */
export async function loadDemoAsset(assetType: AssetType): Promise<string> {
  const path = getDemoAssetPath(assetType);

  try {
    // Story 6.3 Fix: Add short timeout for offline scenarios
    // If fetch takes >5s, assume offline/error and use fallback immediately
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      // Load image from public directory
      const response = await fetch(path, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to load demo asset: ${response.status} ${response.statusText}`);
      }

      // Convert to blob
      const blob = await response.blob();

      // Convert blob to base64 data URL
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = () => {
          const result = reader.result;
          if (typeof result === 'string') {
            resolve(result);
          } else {
            reject(new Error('FileReader returned non-string result'));
          }
        };

        reader.onerror = () => {
          reject(new Error('FileReader failed to read blob'));
        };

        reader.readAsDataURL(blob);
      });
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    // Log error for debugging
    console.error(`Demo asset load failed for ${assetType}:`, error);

    // Return transparent 1x1 PNG as graceful fallback
    // This ensures the game doesn't crash even if demo assets are missing or offline
    const fallback = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

    console.warn(`Using transparent fallback for ${assetType} (possibly offline)`);

    return fallback;
  }
}

/**
 * Check if a demo asset exists and is accessible
 *
 * Validates that the demo asset file can be loaded from the public directory.
 * Useful for pre-flight checks before relying on demo assets.
 *
 * @param {AssetType} assetType - Type of asset to verify
 * @returns {Promise<boolean>} True if asset exists and is accessible
 *
 * @example
 * ```typescript
 * const exists = await verifyDemoAsset('character');
 * if (!exists) {
 *   console.warn('Demo character asset missing!');
 * }
 * ```
 */
export async function verifyDemoAsset(assetType: AssetType): Promise<boolean> {
  const path = getDemoAssetPath(assetType);

  try {
    const response = await fetch(path, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * CustomAssets type for demo mode activation
 * Contains all demo assets and metadata
 */
export interface CustomAssets {
  character: string;
  obstacle: string;
  background: string;
  metadata: {
    source: 'demo';
    timestamp: number;
  };
}

/**
 * Activate full demo mode by loading all demo assets
 *
 * Loads all three demo assets (character, obstacle, background) in parallel
 * and returns them with metadata. Includes performance measurement and logging.
 * Used when generation fails completely or user explicitly requests demo mode.
 *
 * @returns {Promise<CustomAssets>} All demo assets with metadata
 *
 * @example
 * ```typescript
 * const demoAssets = await activateDemoMode();
 * // Returns: { character: "data:image/png...", obstacle: "data:image/png...", background: "data:image/png...", metadata: { source: 'demo', timestamp: 1234567890 } }
 * ```
 */
export async function activateDemoMode(): Promise<CustomAssets> {
  const start = performance.now();

  try {
    // Pre-flight checks: Verify all demo assets exist (Story 6.3 Task 5)
    const [characterExists, obstacleExists, backgroundExists] = await Promise.all([
      verifyDemoAsset('character'),
      verifyDemoAsset('obstacle'),
      verifyDemoAsset('background'),
    ]);

    // Story 6.3 Task 5: Catastrophic failure - ALL demo assets missing
    if (!characterExists && !obstacleExists && !backgroundExists) {
      // Story 6.3 Code Review Fix: Log catastrophic error with critical severity before throwing
      logError({
        errorType: 'catastrophic',
        message: 'All demo assets are missing or inaccessible',
        timestamp: Date.now(),
        severity: 'critical',
        demoModeActivated: 'none',
      });

      const error = new Error('Catastrophic failure: All demo assets are missing or inaccessible');
      error.name = 'CatastrophicError';
      throw error;
    }

    if (!characterExists || !obstacleExists || !backgroundExists) {
      console.warn('Some demo assets missing:', {
        character: characterExists,
        obstacle: obstacleExists,
        background: backgroundExists,
      });
      // Continue anyway - loadDemoAsset will use fallback if needed
    }

    // Load all three demo assets in parallel
    const [character, obstacle, background] = await Promise.all([
      loadDemoAsset('character'),
      loadDemoAsset('obstacle'),
      loadDemoAsset('background'),
    ]);

    const duration = performance.now() - start;
    const timestamp = Date.now();

    // Performance measurement
    console.log('Demo mode activation time:', duration, 'ms');

    // Console logging
    console.log('Full demo mode activated:', { mode: 'full', timestamp });

    // Sentry logging via errorDetectionService
    logError({
      errorType: 'javascript',
      message: 'Demo mode activated (full)',
      timestamp,
      severity: 'info',
      demoModeActivated: 'full',
    });

    return {
      character,
      obstacle,
      background,
      metadata: {
        source: 'demo',
        timestamp,
      },
    };
  } catch (error) {
    console.error('Failed to activate demo mode:', error);
    throw error;
  }
}
